import { useScheduleGaps } from '@/api/tools/getScheduleGaps';
import { PastCustomerBadge } from '@/components/Customer/PastCustomerBadge';
import { DateRangePickerCalendarGrid } from '@/components/DatePicker/DateRangePicker';
import { Link } from '@/components/Elements/Link';
import { ScrollableLayout } from '@/components/Layout';
import Page from '@/components/Layout/Page';
import { Button } from '@/components/ui/elements/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/elements/select';
import { toast } from '@/components/ui/elements/use-toast';
import { PolicyGate } from '@/features/auth/authorization';
import { InsufficientPermissions } from '@/features/auth/components/InsufficientPermissions';
import { ConditionBadge } from '@/features/leads/components/Badges/ConditionBadge';
import { IdBadge } from '@/features/leads/components/Badges/IdBadge';
import { useSearchParams } from '@/hooks/useSearchParams';
import { LocationContext } from '@/providers/location';
import { cn, formatDate } from '@/utils/format';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { getLocalTimeZone, parseDate, today } from '@internationalized/date';
import { useContext, useState } from 'react';
import { Button as AriaButton, Heading, RangeCalendar } from 'react-aria-components';

const searchDefaults = {
  start_date: today(getLocalTimeZone()).add({ days: 7 }),
  end_date: today(getLocalTimeZone()).add({ days: 21 }),
  gap_tolerance_minutes: 120,
};

export const GapFinderPage = () => {
  const { activeLocationId } = useContext(LocationContext);

  const { search, setSearchParams, resetSearchParams } = useSearchParams<{
    start_date: string;
    end_date: string;
    gap_tolerance_minutes?: number;
  }>();

  const [range, setRange] = useState({
    start: search.start_date ? parseDate(search.start_date) : searchDefaults.start_date,
    end: search.end_date ? parseDate(search.end_date) : searchDefaults.end_date,
  });

  const [expandedActivityIds, setExpandedActivityIds] = useState<string[]>([]);

  const { data: activitiesWithGaps } = useScheduleGaps({
    location: activeLocationId,
    search: {
      start_date: search.start_date || searchDefaults.start_date.toString(),
      end_date: search.end_date || searchDefaults.end_date.toString(),
      gap_tolerance_minutes: search.gap_tolerance_minutes || searchDefaults.gap_tolerance_minutes,
    },
  });

  const gapDurationOptions = [
    { label: '30 minutes', value: 30 },
    { label: '45 minutes', value: 45 },
    { label: '1 hour', value: 60 },
    { label: '1.5 hours', value: 90 },
    { label: '2 hours', value: 120 },
    { label: '2.5 hours', value: 150 },
    { label: '3 hours', value: 180 },
  ];

  return (
    <PolicyGate policy="events.view" forbiddenFallback={<InsufficientPermissions />}>
      <PolicyGate policy="leads.view" forbiddenFallback={<InsufficientPermissions />}>
        <ScrollableLayout>
          <Page>
            <Page.Header className="mx-8 my-2">
              <Page.HeaderContent title="Schedule Gap Finder">
                This tool helps identify scheduling gaps in your event calendar and suggests
                potential leads to fill those gaps. The tool considers estimated travel time between
                events when calculating the gap between two events.
              </Page.HeaderContent>
            </Page.Header>
            <Page.Body policy="customers.lists.view">
              <div className="flex flex-row space-x-5 p-3">
                <div>
                  <h3 className="text-lg pl-2">Select a Date Range</h3>
                  <div className="flex flex-col space-y-2 border p-5 bg-background">
                    <RangeCalendar
                      className="w-full"
                      value={range}
                      onChange={(value) => {
                        if (!value.start || !value.end) return;

                        if (value.start.compare(value.end) > 0) {
                          // If start is after end, do not update
                          return;
                        }

                        if (value.end.compare(value.start.add({ days: 31 })) > 0) {
                          toast({
                            title: 'Invalid Date Range',
                            variant: 'destructive',
                            description: 'Please select a date range of 31 days or less.',
                          });
                          return;
                        }

                        setRange(value);

                        setSearchParams({
                          ...search,
                          start_date: value.start.toString(),
                          end_date: value.end.toString(),
                        });
                      }}
                    >
                      <header className="w-full flex flex-row text-center mb-4">
                        <AriaButton slot="previous" className="text-gray-400 hover:text-gray-700">
                          <ChevronRightIcon className="h-5 w-5 rotate-180" />
                        </AriaButton>
                        <Heading className="grow" />
                        <AriaButton slot="next" className="text-gray-400 hover:text-gray-700">
                          <ChevronRightIcon className="h-5 w-5" />
                        </AriaButton>
                      </header>
                      <DateRangePickerCalendarGrid />
                    </RangeCalendar>
                    <div className="mt-4">
                      <label htmlFor="gapSize" className="block text-sm font-medium">
                        Gap Size More Than...
                      </label>
                      <Select
                        onValueChange={(value) => {
                          setSearchParams({
                            ...search,
                            gap_tolerance_minutes: Number(value),
                          });
                        }}
                        defaultValue={
                          search.gap_tolerance_minutes?.toString() ??
                          searchDefaults.gap_tolerance_minutes.toString()
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Gap Size More Than..." />
                        </SelectTrigger>
                        <SelectContent>
                          {gapDurationOptions.map((option) => (
                            <SelectItem
                              key={`option-${option.value}`}
                              value={option.value.toString()}
                            >
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      onClick={() =>
                        setSearchParams({
                          ...search,
                          start_date: range.start.toString(),
                          end_date: range.end.toString(),
                          gap_tolerance_minutes:
                            search.gap_tolerance_minutes ?? searchDefaults.gap_tolerance_minutes,
                        })
                      }
                    >
                      Refresh
                    </Button>
                  </div>
                </div>
                <div className="min-w-96">
                  <h3 className="text-lg">Review Scheduling Gaps</h3>
                  {activitiesWithGaps && activitiesWithGaps.length === 0 && (
                    <div className="border bg-yellow-500/25 p-2 my-2 text-sm text-foreground rounded">
                      No scheduling gaps found for the selected criteria.
                    </div>
                  )}
                  <table className="min-w-full divide-y text-sm">
                    <thead>
                      <tr>
                        <th className="px-3 py-2.5 md:px-6 text-left text-sm font-semibold text-zinc-900 dark:text-zinc-200">
                          Event
                        </th>
                        <th className="py-2.5 text-left text-sm font-semibold text-zinc-900 dark:text-zinc-200">
                          Date/Time
                        </th>
                        <th className="px-3 py-2.5 md:px-6 text-center text-sm font-semibold text-zinc-900 dark:text-zinc-200">
                          Asset
                        </th>
                        <th className="px-0 py-2.5 md:px-2 text-right text-sm font-semibold text-zinc-900 dark:text-zinc-200">
                          Leads
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {activitiesWithGaps?.map((activity) => (
                        <>
                          <tr key={activity.id}>
                            <td className="whitespace-nowrap py-2 px-3 md:py-2.5 md:px-6 text-sm">
                              {activity.name}
                            </td>
                            <td>
                              {formatDate(activity.start_at, 'E MMM d, yyyy h:mma')}-
                              {formatDate(activity.end_at, 'h:mma')}
                            </td>
                            <td className="text-center">{activity.asset.abbr}</td>
                            <td className="text-right">
                              <Button
                                variant="outline"
                                onClick={() => {
                                  if (expandedActivityIds.includes(activity.id.toString())) {
                                    setExpandedActivityIds(
                                      expandedActivityIds.filter(
                                        (id) => id !== activity.id.toString(),
                                      ),
                                    );
                                  } else {
                                    setExpandedActivityIds([
                                      ...expandedActivityIds,
                                      activity.id.toString(),
                                    ]);
                                  }
                                }}
                              >
                                <ChevronRightIcon
                                  className={cn(
                                    'size-4',
                                    expandedActivityIds.includes(activity.id.toString())
                                      ? 'rotate-90'
                                      : '',
                                  )}
                                />
                                <span>{activity.potential_gap_fillers?.length || 0}</span>
                              </Button>
                            </td>
                          </tr>
                          {expandedActivityIds.includes(activity.id.toString()) && (
                            <tr key={`${activity.id}-expand`}>
                              <td colSpan={4} className="bg-gray-50 p-4">
                                <h4 className="font-semibold mb-2">Potential Gap Fillers:</h4>
                                {activity.potential_gap_fillers &&
                                activity.potential_gap_fillers.length > 0 ? (
                                  <div className="divide-y">
                                    {activity.potential_gap_fillers.map((lead) => (
                                      <div
                                        key={`lead-${lead.id}`}
                                        className="flex-col md:flex-row justify-between items-center py-1"
                                      >
                                        <div className="flex items-start gap-x-3">
                                          <p className="font-semibold leading-6 text-gray-900">
                                            <Link
                                              to={
                                                lead.transfer_pending
                                                  ? '/leads/lead/$id/accept-transfer'
                                                  : '/leads/lead/$id/quote'
                                              }
                                              params={{ id: lead.id }}
                                              variant="link"
                                              className="text-md"
                                            >
                                              <PastCustomerBadge
                                                customer={lead.customer}
                                                className="mr-1"
                                              />{' '}
                                              {lead.name || 'Unnamed Lead'}
                                            </Link>
                                          </p>
                                        </div>
                                        <div className="flex space-x-2">
                                          <ConditionBadge lead={lead} />
                                          <IdBadge lead={lead} />
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p>No potential gap fillers found.</p>
                                )}
                              </td>
                            </tr>
                          )}
                        </>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </Page.Body>
          </Page>
        </ScrollableLayout>
      </PolicyGate>
    </PolicyGate>
  );
};
